import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    IndianRupee,
    Receipt,
    Percent,
    HandCoins,
    Store as StoreIcon,
    TrendingUp,
    Search,
    RotateCcw,
    User,
    X,
    Filter,
} from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import { getUserlist } from "../../services/api";
import { fetchStoreLedger } from "../../store/storeSlices/storeLedgerSlice";

const PER_PAGE = 25;

/* ─────────────────────────────────────────────────────────────────────────────
   API shape lives in ONE place. When the response / request changes, only edit
   normalizeRow() (row fields) and buildPayload() (request body).
   ───────────────────────────────────────────────────────────────────────────── */
const normalizeRow = (r) => ({
    id: r.redemption_id,
    date: r.redemption_date,
    referenceNo: r.referneceNo,
    redeemId: r.redem_id,
    customerId: r.user_id ?? r.UserSysId,
    customerName: (r.name || `${r.fname || ""} ${r.lname || ""}`).trim(),
    customerEmail: r.customer_email || "",
    customerMobile: r.customer_mobile || "",
    orderAmount: Number(r.order_amount ?? 0),
    // discount = commission the store pays to GoerOne (flat amount)
    commission: Number(r.discount ?? 0),
    // store_discount = discount the store gives directly to the customer (flat amount)
    storeDiscount: Number(r.store_discount ?? 0),
    // amount the store receives = order amount - (commission + store discount)
    storeReceives: Number(r.order_amount ?? 0) - (Number(r.discount ?? 0) + Number(r.store_discount ?? 0)),
    status: String(r.status ?? "").toLowerCase(),
    settlementStatus: String(r.settlement_status ?? "").toLowerCase(),
    settlementAmount: Number(r.settlement_amount ?? 0),
});

const normalizeCustomer = (c) => ({
    id: c.id,
    name: (c.name || `${c.fname || ""} ${c.lname || ""}`).trim() || "Customer",
    email: c.email || "",
    mobile: c.mobile || "",
});

const buildPayload = (f) => ({
    customer_id: Number(f.customerId) || 0,
    referneceNo: (f.referenceNo || "").trim(),
    FromDate: f.fromDate ? dayjs(f.fromDate).startOf("day").toISOString() : null,
    ToDate: f.toDate ? dayjs(f.toDate).endOf("day").toISOString() : null,
});

const defaultFilters = () => ({
    fromDate: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    toDate: dayjs().format("YYYY-MM-DD"),
    customerId: "",
    referenceNo: "",
});

/* Static class maps so Tailwind can see every class name */
const ACCENT = {
    amber: { chip: "bg-amber-50 text-amber-600", bar: "bg-amber-400" },
    green: { chip: "bg-green-50 text-green-600", bar: "bg-green-500" },
    blue: { chip: "bg-blue-50 text-blue-600", bar: "bg-blue-500" },
    purple: { chip: "bg-purple-50 text-purple-600", bar: "bg-purple-500" },
    rose: { chip: "bg-rose-50 text-rose-600", bar: "bg-rose-500" },
    slate: { chip: "bg-slate-100 text-slate-600", bar: "bg-slate-400" },
};

const STATUS_STYLE = {
    completed: "bg-green-50 text-green-700 border border-green-200",
    pending: "bg-amber-50 text-amber-700 border border-amber-200",
    cancelled: "bg-red-50 text-red-700 border border-red-200",
    failed: "bg-red-50 text-red-700 border border-red-200",
    settled: "bg-green-50 text-green-700 border border-green-200",
};
const STATUS_FALLBACK = "bg-gray-100 text-gray-700 border border-gray-200";

const money = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (v) => (v === null || v === undefined || v === "" || isNaN(Number(v)) ? "-" : money.format(Number(v)));
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const inputCls = "w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none";

const StatCard = ({ icon: Icon, label, value, hint, accent = "amber" }) => (
    <div className="relative overflow-hidden bg-white border rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className={`absolute left-0 top-0 h-full w-1 ${ACCENT[accent].bar}`} />
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-gray-900 truncate">{value}</p>
                {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
            </div>
            <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${ACCENT[accent].chip}`}>
                <Icon size={18} />
            </div>
        </div>
    </div>
);

const StorePanelHome = () => {
    useDocumentTitle("Welcome to Store | Dashboard");
    const dispatch = useDispatch();
    const { rows, totals, loading, error, totalPages, total, from, to } = useSelector((state) => state.storeLedger);

    const [draft, setDraft] = useState(defaultFilters);
    const [applied, setApplied] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false); // hidden by default

    // Customer search (name / mobile / email) with suggestions - same pattern as RewardLedgerView
    const [customerQuery, setCustomerQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggesting, setSuggesting] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const customerBoxRef = useRef(null);
    const searchSeq = useRef(0);
    const keyword = customerQuery.trim();

    useEffect(() => {
        const seq = ++searchSeq.current; // invalidates any in-flight request
        if (draft.customerId || keyword.length < 3) {
            setSuggestions([]);
            setSuggesting(false);
            return;
        }
        setSuggesting(true);
        const t = setTimeout(async () => {
            try {
                const res = await getUserlist(10, 1, keyword);
                if (seq !== searchSeq.current) return;
                const list = res?.data?.data || res?.data || [];
                setSuggestions((Array.isArray(list) ? list : []).map(normalizeCustomer));
            } catch {
                if (seq === searchSeq.current) setSuggestions([]);
            } finally {
                if (seq === searchSeq.current) setSuggesting(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [keyword, draft.customerId]);

    useEffect(() => {
        const onDown = (ev) => {
            if (customerBoxRef.current && !customerBoxRef.current.contains(ev.target)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const handleCustomerInput = (e) => {
        setCustomerQuery(e.target.value);
        setDraft((prev) => ({ ...prev, customerId: "" })); // typing again un-selects
        setShowSuggestions(true);
    };

    const selectCustomer = (c) => {
        setDraft((prev) => ({ ...prev, customerId: c.id }));
        setCustomerQuery(c.name);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const clearCustomer = () => {
        setDraft((prev) => ({ ...prev, customerId: "" }));
        setCustomerQuery("");
        setSuggestions([]);
    };

    const load = () => dispatch(fetchStoreLedger({ perPage: PER_PAGE, page: currentPage, filters: buildPayload(applied) }));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currentPage, applied]);

    const handleDraft = (e) => setDraft((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) {
            toastr.error("From date cannot be after To date");
            return;
        }
        if (keyword && !draft.customerId) {
            toastr.error("Pick a customer from the suggestions, or clear the customer search");
            return;
        }
        setCurrentPage(1);
        setApplied({ ...draft });
    };

    const handleReset = () => {
        const fresh = defaultFilters();
        setDraft(fresh);
        setCustomerQuery("");
        setSuggestions([]);
        setCurrentPage(1);
        setApplied(fresh);
    };

    const entries = rows.map(normalizeRow);
    const activeFilters = (applied.customerId ? 1 : 0) + (applied.referenceNo.trim() ? 1 : 0);
    const storeName = rows[0]?.store_name;

    const sales = Number(totals.total_order_amount) || 0;
    const commission = Number(totals.total_discount) || 0;
    const storeDiscount = Number(totals.total_store_discount) || 0;
    const netSales = sales - storeDiscount - commission;
    const avgOrder = total > 0 ? sales / total : 0;

    return (
        <div className="w-full space-y-4">
            {/* Header */}
            <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
                <div className="flex items-center gap-2">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                        <StoreIcon size={22} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold leading-tight">Store Dashboard</h3>
                        <p className="text-sm text-white/90">
                            {storeName ? `${storeName} · ` : ""}
                            {dayjs(applied.fromDate).format("DD MMM YYYY")} – {dayjs(applied.toDate).format("DD MMM YYYY")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowFilters((v) => !v)}
                        aria-expanded={showFilters}
                        title="Filter"
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
                    >
                        <Filter size={16} />
                        {showFilters ? "Hide filters" : "Filters"}
                        {activeFilters > 0 && (
                            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">
                                {activeFilters}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Filters (collapsed by default) */}
            {showFilters && (
            <form onSubmit={handleApply} className="bg-white shadow-lg rounded-2xl p-4 space-y-2">
                <div ref={customerBoxRef} className="relative w-full">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
                    <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={customerQuery}
                            onChange={handleCustomerInput}
                            onFocus={() => setShowSuggestions(true)}
                            onKeyDown={(ev) => {
                                if (ev.key === "Enter" && keyword && !draft.customerId) ev.preventDefault();
                                if (ev.key === "Escape") setShowSuggestions(false);
                            }}
                            placeholder="Search by name, mobile or email (min 3 characters)"
                            autoComplete="off"
                            className={`${inputCls} pl-9 pr-9`}
                        />
                        {customerQuery && (
                            <button type="button" onClick={clearCustomer} title="Clear customer" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    {showSuggestions && !draft.customerId && keyword.length >= 3 && (
                        <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-64 overflow-auto">
                            {suggesting ? (
                                <li className="px-3 py-2 text-sm text-gray-500">Searching...</li>
                            ) : suggestions.length > 0 ? (
                                suggestions.map((c) => (
                                    <li
                                        key={c.id}
                                        onClick={() => selectCustomer(c)}
                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                        <div className="text-sm font-medium">{c.name}</div>
                                        <div className="text-xs text-gray-500">{[c.email, c.mobile].filter(Boolean).join(" | ")}</div>
                                    </li>
                                ))
                            ) : (
                                <li className="px-3 py-2 text-sm text-gray-500">No customers found</li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From date</label>
                        <input type="date" name="fromDate" value={draft.fromDate} onChange={handleDraft} className={inputCls} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">To date</label>
                        <input type="date" name="toDate" value={draft.toDate} min={draft.fromDate || undefined} onChange={handleDraft} className={inputCls} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Reference no.</label>
                        <input type="text" name="referenceNo" value={draft.referenceNo} onChange={handleDraft} placeholder="e.g. E5YVW3ACTU" className={inputCls} />
                    </div>
                    <div className="flex items-end gap-2">
                        <button type="submit" className="flex-1 inline-flex items-center justify-center gap-1 py-2 px-3 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors text-sm">
                            <Search size={16} /> Search
                        </button>
                        <button type="button" onClick={handleReset} title="Reset filters" className="py-2 px-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
                            <RotateCcw size={16} />
                        </button>
                    </div>
                </div>
            </form>
            )}

            {error && (
                <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                    <span>{error}</span>
                    <button type="button" onClick={load} className="font-medium underline">Retry</button>
                </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                <StatCard icon={IndianRupee} accent="amber" label="Total sales" value={`₹${fmt(sales)}`} hint={`${total} transaction${total === 1 ? "" : "s"}`} />
                <StatCard icon={Percent} accent="rose" label="Commission to GoerOne" value={`₹${fmt(commission)}`} hint={`${pct(commission, sales)}% of sales`} />
                <StatCard icon={HandCoins} accent="green" label="Store discount to customers" value={`₹${fmt(storeDiscount)}`} hint={`${pct(storeDiscount, sales)}% of sales`} />
                <StatCard icon={Receipt} accent="blue" label="Transactions" value={total} hint={`Avg. order ₹${fmt(avgOrder)}`} />
                <StatCard icon={TrendingUp} accent="slate" label="Net sales" value={`₹${fmt(netSales)}`} hint="Sales − (commission + customer discount)" />
            </div>

            {/* Transactions */}
            <div className="bg-white shadow-lg rounded-2xl p-3">
                <h3 className="text-lg font-semibold mb-3">Transactions</h3>
                <div className="overflow-auto border rounded-xl">
                    <table className="w-full min-w-[900px] text-sm text-left">
                        <thead className="text-gray-700 bg-gray-100">
                            <tr>
                                <th className="p-2">Customer &amp; date</th>
                                <th className="p-2">Reference</th>
                                <th className="p-2 text-right">Order amount</th>
                                <th className="p-2 text-right">Customer discount</th>
                                <th className="p-2 text-right">Commission</th>
                                <th className="p-2 text-right">Net sales</th>
                                <th className="p-2">Status</th>
                                {/* <th className="p-3">Settlement</th> */}
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {loading ? (
                                <tr>
                                    <td className="p-2" colSpan={7}>
                                        <ShimmerLoader />
                                        <ShimmerLoader />
                                        <ShimmerLoader />
                                    </td>
                                </tr>
                            ) : entries.length > 0 ? (
                                entries.map((e) => (
                                    <tr key={e.id} className="hover:bg-gray-50 align-top">
                                        <td className="p-2 border-l-4 border-green-500">
                                            <div className="font-medium">{e.customerName || `Customer #${e.customerId}`}</div>
                                            {(e.customerEmail || e.customerMobile) && (
                                                <div className="text-xs text-gray-500 break-all">{[e.customerEmail, e.customerMobile].filter(Boolean).join(" | ")}</div>
                                            )}
                                            <div className="text-xs text-gray-700 mt-1">
                                                {e.date ? dayjs(e.date).format("DD MMM YYYY, hh:mm A") : "-"}
                                            </div>
                                        </td>
                                        <td className="p-2">
                                            <div className="font-medium break-all">{e.referenceNo || "-"}</div>
                                            <div className="text-xs text-gray-500 break-all">{e.redeemId}</div>
                                        </td>
                                        <td className="p-2 text-right font-semibold">₹{fmt(e.orderAmount)}</td>
                                        <td className="p-2 text-right font-semibold text-green-600">₹{fmt(e.storeDiscount)}</td>
                                        <td className="p-2 text-right font-semibold text-rose-600">₹{fmt(e.commission)}</td>
                                        <td className="p-2 text-right font-semibold text-gray-900">₹{fmt(e.storeReceives)}</td>
                                        <td className="p-2">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[e.status] || STATUS_FALLBACK}`}>
                                                {e.status || "-"}
                                            </span>
                                        </td>
                                        {/* <td className="p-3">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[e.settlementStatus] || STATUS_FALLBACK}`}>
                                                {e.settlementStatus || "-"}
                                            </span>
                                            <div className="text-xs text-gray-500 mt-1">₹{fmt(e.settlementAmount)}</div>
                                        </td> */}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="p-6 text-center text-gray-500" colSpan={7}>No transactions found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-3">
                    <p className="text-xs text-gray-500">{!loading && total > 0 ? `Showing ${from}–${to} of ${total}` : ""}</p>
                    <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
            </div>
        </div>
    );
};

export default StorePanelHome;